import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSearchByCategoryQuery, useSearchStream } from '../../../api';
import { SEARCH_CATEGORY_ORDER } from '../../../model/categories';
import { getRecentSearches, pushRecentSearch } from '../../../model/recent';
import { getSearchTargetRoute } from '../../../model/targetRoute';
import { SearchCategory, SearchHitEntry, SearchLaneState } from '../../../model/types';
import { SearchTabKey, SearchTabOption } from '../../SearchResultTabs';

const SEARCH_GROUP_LIMIT = 4;

export interface SearchResultGroup {
  category: SearchCategory;
  entries: SearchHitEntry[];
  totalCount: number;
  startIndex: number;
}

interface SearchPanelControllerOptions {
  query: string;
  onQueryChange: (query: string) => void;
  onClose: () => void;
}

export function useSearchPanelController({
  query,
  onQueryChange,
  onClose,
}: SearchPanelControllerOptions) {
  const navigate = useNavigate();

  const stream = useSearchStream(query);
  const { populatedLanes, isQueryLongEnough, trimmedQuery, lanes } = stream;

  const [activeTab, setActiveTab] = useState<SearchTabKey>('ALL');
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveTab('ALL');
    setActiveIndex(0);
  }, [trimmedQuery]);

  const changeTab = useCallback((tab: SearchTabKey) => {
    setActiveTab(tab);
    setActiveIndex(0);
  }, []);

  const categoryQuery = useSearchByCategoryQuery(
    trimmedQuery,
    activeTab === 'ALL' ? null : activeTab,
  );

  const categoryHits = useMemo(
    () => (categoryQuery.data?.pages ?? []).flatMap((page) => page.content),
    [categoryQuery.data?.pages],
  );

  const recentSearches = useMemo(() => getRecentSearches(), []);

  const groups = useMemo<SearchResultGroup[]>(() => {
    let startIndex = 0;
    return populatedLanes.map(
      ({ category, lane }: { category: SearchCategory; lane: SearchLaneState }) => {
        const entries = lane.hits
          .slice(0, SEARCH_GROUP_LIMIT)
          .map((hit): SearchHitEntry => ({ category, hit }));
        const group = { category, entries, totalCount: lane.totalCount, startIndex };
        startIndex += entries.length;
        return group;
      },
    );
  }, [populatedLanes]);

  const rows = useMemo<SearchHitEntry[]>(() => {
    if (!isQueryLongEnough) return [];
    if (activeTab === 'ALL') return groups.flatMap((group) => group.entries);
    return categoryHits.map((hit) => ({ category: activeTab, hit }));
  }, [isQueryLongEnough, activeTab, groups, categoryHits]);

  useEffect(() => {
    setActiveIndex((index) => Math.max(0, Math.min(index, rows.length - 1)));
  }, [rows.length]);

  const tabs = useMemo<SearchTabOption[]>(() => {
    const perCategory = SEARCH_CATEGORY_ORDER.map((category) => ({
      key: category as SearchTabKey,
      count: lanes[category]?.totalCount ?? 0,
    }));
    const total = perCategory.reduce((sum, tab) => sum + tab.count, 0);
    return [{ key: 'ALL', count: total }, ...perCategory];
  }, [lanes]);

  const totalResults = tabs[0].count;

  const openEntry = useCallback(
    (entry: SearchHitEntry) => {
      const route = getSearchTargetRoute(entry.hit.target);
      if (!route) return;
      if (trimmedQuery) pushRecentSearch(trimmedQuery);
      onClose();
      navigate(route);
    },
    [navigate, trimmedQuery, onClose],
  );

  useEffect(() => {
    const cycleTab = (direction: 1 | -1) => {
      const enabled = tabs
        .filter((tab) => tab.key === 'ALL' || tab.count > 0)
        .map((tab) => tab.key);
      const currentIndex = enabled.indexOf(activeTab);
      const nextIndex = (currentIndex + direction + enabled.length) % enabled.length;
      changeTab(enabled[nextIndex]);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      const isSearchInput = Boolean(target && 'searchInput' in target.dataset);
      if (
        target &&
        !isSearchInput &&
        (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.isContentEditable)
      )
        return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex((index) => Math.min(rows.length - 1, index + 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex((index) => Math.max(0, index - 1));
          break;
        case 'Enter':
          if (isSearchInput || target === document.body) {
            event.preventDefault();
            if (rows[activeIndex]) openEntry(rows[activeIndex]);
          }
          break;
        case 'Tab':
          if (isQueryLongEnough) {
            event.preventDefault();
            cycleTab(event.shiftKey ? -1 : 1);
          }
          break;
        case 'Escape':
          event.preventDefault();
          if (query) onQueryChange('');
          else onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    tabs,
    activeTab,
    changeTab,
    rows,
    activeIndex,
    openEntry,
    isQueryLongEnough,
    query,
    onQueryChange,
    onClose,
  ]);

  const totalElements = categoryQuery.data?.pages[0]?.totalElements ?? 0;

  return {
    stream,
    tabs,
    totalResults,
    activeTab,
    changeTab,
    groups,
    rows,
    activeIndex,
    setActiveIndex,
    openEntry,
    recentSearches,
    categoryQuery,
    totalElements,
  };
}
